import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Maximize, ZoomIn, MessageSquare, X, Send, Smartphone, Monitor, MonitorSmartphone, RefreshCw, Globe, ArrowRight, ArrowLeft, ChevronDown, Plus, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const STORAGE_KEY = 'preview_session';
// Merkt sich die vom Crawler/Link-Scan gefundenen (also tatsächlich existierenden)
// Unterseiten pro Vorschau-Code über Seiten-Reloads hinweg. "_v2"-Suffix verwirft
// bewusst alten, evtl. mit versehentlich eingetippten Pfaden verunreinigten Stand
// aus der Vorgängerversion (als hier noch manuell navigierte Pfade mit gespeichert wurden).
const subpagesStorageKey = (previewCode) => `preview_subpages_v2_${previewCode}`;
// Statische Assets (CSS/Bilder/Fonts/Icons etc.) sind keine "Unterseiten" — Kundenseiten
// binden die meist mit absoluten Pfaden ein, die sonst ungefiltert im Dropdown landen würden.
const ASSET_EXT_RE = /\.(css|js|mjs|png|jpe?g|gif|svg|webp|avif|ico|woff2?|ttf|eot|map|json|xml|txt|pdf|mp4|webm|mp3|zip)$/i;
// Fester Vorschlag bei JEDER Vorschau, unabhängig vom Crawler/manuellen Merken — fast
// alle Kundenprojekte haben einen Admin-Bereich, der (absichtlich) nirgendwo verlinkt
// und daher für den Crawler grundsätzlich nicht auffindbar ist.
const DEFAULT_SUBPAGES = ['admin'];

function ChatWidget({ code }) {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const bottomRef = useRef(null);
    const textareaRef = useRef(null);

    const loadMessages = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/preview/chat?code=${encodeURIComponent(code)}`);
            if (res.ok) {
                const data = await res.json();
                const msgs = Array.isArray(data) ? data : [];
                const newUnread = msgs.filter(m => m.sender === 'admin' && !m.read_by_customer);
                if (!open && newUnread.length > 0) {
                    setHasUnread(true);
                    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                        try { new Notification('Neue Nachricht von Lino 💬', { body: newUnread[newUnread.length - 1].text.slice(0, 120) }); } catch {}
                    }
                }
                if (open) setHasUnread(false);
                setMessages(msgs);
            }
        } catch {}
    }, [code, open]);

    useEffect(() => {
        loadMessages();
    }, [loadMessages]);

    useEffect(() => {
        if (open) {
            loadMessages();
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                Notification.requestPermission();
            }
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const id = setInterval(loadMessages, 15000);
        return () => clearInterval(id);
    }, [open, loadMessages]);

    useEffect(() => {
        if (open) {
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
    }, [messages.length]);

    const send = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await fetch(`${API}/api/preview/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, text: text.trim() }),
            });
            setText('');
            await loadMessages();
        } catch {}
        setSending(false);
        textareaRef.current?.focus();
    };

    const fmtTime = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div style={{ position: 'fixed', top: '16px', right: '20px', zIndex: 1000 }}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px',
                            width: '320px', boxSizing: 'border-box',
                            boxShadow: '0 8px 28px rgba(0,0,0,0.35)', marginBottom: '10px',
                            display: 'flex', flexDirection: 'column', maxHeight: '500px',
                        }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <MessageSquare size={15} color="var(--accent)" />
                                <span style={{ fontSize: '13px', fontWeight: 700 }}>Chat / Feedback</span>
                            </div>
                            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 0 }}>
                                <X size={16} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {messages.length === 0 && (
                                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', padding: '24px 0' }}>
                                    Noch keine Nachrichten.<br />Schreib uns einfach!
                                </div>
                            )}
                            {messages.map(m => (
                                <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'customer' || !m.sender ? 'flex-end' : 'flex-start' }}>
                                    <div style={{
                                        maxWidth: '85%',
                                        padding: '8px 12px',
                                        borderRadius: (m.sender === 'customer' || !m.sender) ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                        background: (m.sender === 'customer' || !m.sender) ? 'var(--accent)' : 'var(--bg-secondary)',
                                        color: (m.sender === 'customer' || !m.sender) ? '#000' : 'var(--text-primary)',
                                        fontSize: '13px', lineHeight: 1.5,
                                    }}>
                                        {m.sender === 'admin' && (
                                            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--accent)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Lino
                                            </div>
                                        )}
                                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                                        <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '4px', textAlign: 'right' }}>
                                            {fmtTime(m.created_at)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div ref={bottomRef} />
                        </div>

                        {/* Input */}
                        <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <textarea
                                ref={textareaRef}
                                value={text}
                                onChange={e => setText(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                                placeholder="Nachricht schreiben… (Enter zum Senden)"
                                style={{
                                    flex: 1, background: 'var(--bg-secondary)', border: '1.5px solid var(--border)',
                                    borderRadius: '8px', color: 'var(--text-primary)', fontFamily: 'inherit',
                                    fontSize: '13px', padding: '8px 10px', resize: 'none', outline: 'none',
                                    minHeight: '36px', maxHeight: '90px', lineHeight: 1.4,
                                }}
                                rows={1}
                            />
                            <button onClick={send} disabled={sending || !text.trim()}
                                style={{
                                    background: text.trim() && !sending ? 'var(--accent)' : 'var(--bg-secondary)',
                                    border: 'none', borderRadius: '8px',
                                    cursor: text.trim() && !sending ? 'pointer' : 'default',
                                    padding: '8px 12px',
                                    color: text.trim() && !sending ? '#000' : 'var(--text-secondary)',
                                    transition: 'all 0.15s', flexShrink: 0,
                                    display: 'flex', alignItems: 'center',
                                }}>
                                <Send size={15} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <button onClick={() => { setOpen(o => !o); if (!open) setHasUnread(false); }} title="Chat / Feedback"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '44px', height: '44px', borderRadius: '50%', marginLeft: 'auto',
                    background: open ? 'var(--accent)' : 'var(--card-bg)',
                    border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.3)', transition: 'all 0.15s',
                    position: 'relative',
                }}>
                <MessageSquare size={19} color={open ? '#000' : 'var(--accent)'} />
                {!open && hasUnread && (
                    <span style={{
                        position: 'absolute', top: '-3px', right: '-3px',
                        background: '#ef4444', width: '10px', height: '10px', borderRadius: '50%',
                        border: '2px solid var(--card-bg)',
                    }} />
                )}
            </button>
        </div>
    );
}

// Skaliert ein fix bemaßtes Design (refWidth x refHeight) exakt proportional auf den
// verfügbaren Platz herunter (nie hoch) — per ResizeObserver auf echte Pixelgrößen statt
// vw/vh-Berechnungen. Dadurch bleiben alle Schatten/Verläufe im Mockup pixelgenau im
// richtigen Verhältnis zueinander, egal wie das Browserfenster oder der Zoom steht.
function useScaleToFit(containerRef, refWidth, refHeight) {
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const fit = () => {
            // Bewusst kein Deckel bei 1 mehr: im Zoom-Vollbild soll das Mockup auf großen
            // Bildschirmen auch über seine "native" Design-Größe hinaus wachsen dürfen —
            // das Seitenverhältnis bleibt dabei durch die gemeinsame min()-Berechnung erhalten.
            const s = Math.min(el.clientWidth / refWidth, el.clientHeight / refHeight);
            if (s > 0) setScale(s);
        };
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(el);
        return () => ro.disconnect();
    }, [containerRef, refWidth, refHeight]);
    return scale;
}

// Kurze "Boot"-Phase beim ERSTEN Anzeigen eines Geräte-Mockups (Code eingeben, oder
// PC/Handy umschalten — beides mountet die Komponente frisch): rein zeitbasiert (nicht
// vom iframe-onLoad-Event abhängig — das feuert bei sehr schnellen/lokalen Ladezeiten
// mitunter, bevor React den Listener überhaupt anhängt, sonst würde die Animation dann
// nie verschwinden). Läuft bewusst nur EINMAL pro Mount, nicht bei jedem Klick auf den
// Reload-Knopf — sonst nervt sie bei einem einfachen "Seite neu laden" nur.
function useBootReady(minMs = 2000) {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setReady(true), minMs);
        return () => clearTimeout(t);
    }, [minMs]);
    return ready;
}

// Tippt einen Text zeichenweise ein (Terminal-Effekt). Tempo skaliert leicht mit der
// Textlänge, bleibt aber in einem sinnvollen Rahmen (kurze Titel nicht zu hektisch,
// lange nicht zu träge).
function useTypewriter(text, active) {
    const [typed, setTyped] = useState('');
    useEffect(() => {
        if (!active || !text) { setTyped(''); return; }
        setTyped('');
        let i = 0;
        const stepMs = Math.max(26, Math.min(65, 900 / text.length));
        const id = setInterval(() => {
            i += 1;
            setTyped(text.slice(0, i));
            if (i >= text.length) clearInterval(id);
        }, stepMs);
        return () => clearInterval(id);
    }, [text, active]);
    return typed;
}

// Boot-Overlay im Stil der Website selbst (Monospace, oranger Akzent, "&gt;"-Prompt wie
// beim versteckten </>-Logo-Symbol) statt eines generischen Spinners — tippt den Titel
// der Vorschau ein, dann Übergang zur echten Seite. fontSize steuert die Textgröße je
// nach Gerät (Handy braucht kleinere Schrift als der Monitor).
function BootOverlay({ ready, label, fontSize = 15 }) {
    const typed = useTypewriter(label, !ready);
    return (
        <AnimatePresence>
            {!ready && (
                <motion.div initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
                    style={{
                        position: 'absolute', inset: 0, zIndex: 10, background: '#05060a',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px',
                    }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
                        style={{ width: '26px', height: '26px', borderRadius: '50%', border: '3px solid rgba(251,146,60,0.18)', borderTopColor: '#fb923c' }} />
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: `${fontSize}px`, color: '#fb923c', letterSpacing: '0.01em', display: 'flex', alignItems: 'center', maxWidth: '90%' }}>
                        <span style={{ opacity: 0.45, marginRight: '7px' }}>&gt;</span>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label ? typed : 'Lädt'}</span>
                        <motion.span animate={{ opacity: [1, 1, 0, 0] }} transition={{ duration: 1, repeat: Infinity, times: [0, 0.5, 0.5, 1] }}
                            style={{ display: 'inline-block', width: '0.55em', marginLeft: '2px' }}>▌</motion.span>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// Reload-Panel als kleine "Bildschirm-Erweiterung" rechts neben dem Gerät — eigener
// Mini-Monitor im selben Alu-Rahmen-Look, statt eines winzigen, kaum sichtbaren Knopfs
// im Gehäuse selbst. Wird als Kind des Rahmen-divs (position:relative) eingehängt,
// dadurch unabhängig von der Spaltenzentrierung (Standfuß bleibt mittig unterm Gerät).
function MiniReloadPanel({ onClick, size = 56, gap = 26 }) {
    return (
        <div style={{
            position: 'absolute', left: `calc(100% + ${gap}px)`, top: '50%', transform: 'translateY(-50%)',
            width: `${size}px`, height: `${size}px`, borderRadius: '13px', padding: '5px', flexShrink: 0,
            background: 'linear-gradient(180deg,#787f87 0%,#454a50 20%,#26292c 65%,#161719 100%)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), inset 0 0 0 1px rgba(0,0,0,0.3), 0 24px 46px -12px rgba(0,0,0,0.75)',
        }}>
            <button onClick={onClick} title="Seite im Gerät neu laden" aria-label="Seite im Gerät neu laden"
                style={{
                    width: '100%', height: '100%', borderRadius: '8px', border: 'none', cursor: 'pointer', padding: 0,
                    background: '#0a0b0d', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)', transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1a1c1f'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#0a0b0d'; }}>
                <RefreshCw size={Math.round(size * 0.4)} color="#fb923c" strokeWidth={2.2} />
            </button>
        </div>
    );
}

// Reines Präsentations-Markup des Monitors ohne eigene Skalierung — nimmt reloadKey/
// onReloadClick von außen entgegen, damit ein gemeinsamer Elternteil (Komplett-Modus)
// Monitor und Handy synchron neu laden kann. showReloadPanel blendet das eigene
// Mini-Panel aus, wenn ein umgebender Modus bereits ein gemeinsames anzeigt.
function MonitorDevice({ fullUrl, onIframeLoad, title, reloadKey, onReloadClick, showReloadPanel = true, iframeRef, domainSlug, subPath, onNavigate, onPin, onBack, canGoBack, subpages, hideStand = false, domainBarCollapsed = false, onToggleDomainBar }) {
    const ready = useBootReady();
    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ position: 'absolute', left: '50%', top: '60%', transform: 'translate(-50%,0)', width: '1560px', height: '300px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(130,160,200,0.11) 0%, rgba(90,120,160,0.04) 45%, rgba(0,0,0,0) 72%)', filter: 'blur(34px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', padding: '5px 5px 0', borderRadius: '11px 11px 9px 9px', background: 'linear-gradient(180deg,#8f959c 0%,#4c5157 2%,#2c3034 30%,#212427 80%,#15171a 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), inset 1px 0 0 rgba(255,255,255,0.12), inset -1px 0 0 rgba(255,255,255,0.12), 0 1px 3px rgba(0,0,0,0.7), 0 80px 110px -45px rgba(0,0,0,0.92)' }}>
                {onNavigate && <DomainBarMount domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} width={430} collapsed={domainBarCollapsed} onToggleCollapse={onToggleDomainBar} />}
                <div style={{ position: 'absolute', left: '70px', right: '70px', top: 'calc(100% - 4px)', height: '20px', borderRadius: '0 0 20px 20px', background: 'linear-gradient(180deg,#3b4046 0%,#1b1d20 70%,#0f1012 100%)', boxShadow: '0 12px 22px rgba(0,0,0,0.6)', zIndex: -1 }} />

                <div style={{ position: 'relative', width: '1600px', height: '900px', borderRadius: '6px', overflow: 'hidden', background: '#000', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.07)' }}>
                    <iframe ref={iframeRef} key={reloadKey} src={fullUrl} onLoad={onIframeLoad} title="Preview" style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#111' }} />
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: 'inset 0 0 90px rgba(0,0,0,0.4), inset 0 0 20px rgba(0,0,0,0.3)' }} />
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', mixBlendMode: 'screen', opacity: 0.5, background: 'linear-gradient(106deg, rgba(214,226,244,0.18) 0%, rgba(190,205,228,0.06) 11%, rgba(255,255,255,0) 27%, rgba(255,255,255,0) 68%, rgba(200,214,236,0.045) 88%, rgba(214,226,244,0.12) 100%)' }} />
                    <div style={{ position: 'absolute', left: '-8%', top: '-30%', width: '52%', height: '120%', pointerEvents: 'none', mixBlendMode: 'screen', opacity: 0.4, transform: 'rotate(-14deg)', background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.03) 40%, rgba(255,255,255,0) 70%)', filter: 'blur(26px)' }} />
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(255,255,255,0.05)' }} />
                    <BootOverlay ready={ready} label={title} fontSize={16} />
                </div>

                <div style={{ height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#7fe0a4', boxShadow: '0 0 9px rgba(127,224,164,0.8)' }} />
                </div>

                {showReloadPanel && <MiniReloadPanel onClick={onReloadClick} size={60} gap={30} />}
            </div>

            {/* Standfuß + weiche Kontaktschatten — im Zoom-Vollbild ausgeblendet, damit der
                Bildschirm selbst nicht wegen des Platzbedarfs für den Fuß kleiner skaliert
                werden muss (siehe hideStand in RealisticMonitor/CombinedDevices). */}
            {!hideStand && (
                <>
                    <div style={{ position: 'relative', width: '46px', height: '210px', marginTop: '-3px', borderRadius: '3px 3px 6px 6px', background: 'linear-gradient(90deg,#141517 0%,#4a5158 18%,#8b9299 42%,#5b6167 62%,#2a2e32 84%,#121315 100%)', boxShadow: '0 20px 34px rgba(0,0,0,0.6)' }} />

                    <div style={{ position: 'relative', width: '440px', height: '16px', marginTop: '-4px', borderRadius: '50% / 70%', background: 'linear-gradient(180deg,#9aa1a8 0%,#5d6369 22%,#33373b 60%,#141517 100%)', boxShadow: '0 16px 26px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.4)' }} />
                    <div style={{ width: '410px', height: '7px', marginTop: '-3px', borderRadius: '50%', background: 'linear-gradient(180deg,#1a1c1f 0%,#0b0c0e 100%)' }} />

                    <div style={{ width: '760px', height: '44px', marginTop: '-20px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.3) 42%, rgba(0,0,0,0) 72%)', filter: 'blur(13px)' }} />
                    <div style={{ width: '1220px', height: '150px', marginTop: '-72px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 68%)', filter: 'blur(36px)' }} />
                </>
            )}
        </div>
    );
}

// Realistisches Monitor-Mockup (Design-Vorlage von Lino aus Claude Design übernommen):
// Alu-Rahmen mit Lichtkante, Glas-Screen mit diagonalem Glare + Vignette, Standfuß,
// weiche Kontaktschatten auf dem "Tisch". Eigenständiger PC-Modus mit eigenem
// Skalierungs-Container und eigenem Reload-Zähler.
function RealisticMonitor({ fullUrl, onIframeLoad, title, domainSlug, subPath, onNavigate, onPin, onBack, canGoBack, subpages, zoomMode = false, domainBarCollapsed, onToggleDomainBar }) {
    const containerRef = useRef(null);
    // refHeight von 1360 auf 1460 angehoben: die Adressleiste + Verbindungsspitze ragen
    // oben über den Rahmen hinaus (~100px in nativen Koordinaten) und wurden von der alten
    // Referenzhöhe nicht mitgerechnet — dadurch stand der Rahmen zu weit oben im skalierten
    // Container und die Leiste wurde vom umgebenden overflow:hidden abgeschnitten.
    // Im Zoom-Vollbild fällt der Standfuß weg (hideStand) — die Referenzhöhe schrumpft
    // entsprechend, sonst würde die Skalierung weiterhin Platz für den (gar nicht mehr
    // gezeichneten) Fuß freihalten und der Bildschirm bliebe unnötig klein.
    const scale = useScaleToFit(containerRef, 2000, zoomMode ? 1050 : 1460);
    const [reloadKey, setReloadKey] = useState(0);
    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            {/* top:55% statt 50% -> mehr Freiraum oberhalb des Rahmens für die Adressleiste. */}
            <div style={{ position: 'absolute', left: '50%', top: '55%', transform: `translate(-50%,-50%) scale(${scale})`, transformOrigin: 'center center' }}>
                <MonitorDevice fullUrl={fullUrl} onIframeLoad={onIframeLoad} title={title} reloadKey={reloadKey} onReloadClick={() => setReloadKey(k => k + 1)} domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} hideStand={zoomMode} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={onToggleDomainBar} />
            </div>
        </div>
    );
}

// Reines Präsentations-Markup des Handys, analog zu MonitorDevice.
function PhoneDevice({ fullUrl, onIframeLoad, title, reloadKey, onReloadClick, showReloadPanel = true, iframeRef, domainSlug, subPath, onNavigate, onPin, onBack, canGoBack, subpages, domainBarCollapsed = false, onToggleDomainBar }) {
    const ready = useBootReady();
    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ position: 'absolute', left: '50%', top: '48%', transform: 'translate(-50%,-50%)', width: '520px', height: '520px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(130,160,200,0.10) 0%, rgba(90,120,160,0.03) 45%, rgba(0,0,0,0) 72%)', filter: 'blur(30px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', width: '380px', height: '800px', padding: '12px', borderRadius: '54px', background: 'linear-gradient(155deg,#8f959c 0%,#54595f 12%,#2c3034 40%,#1b1d20 70%,#0e0f11 100%)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), inset 1px 0 0 rgba(255,255,255,0.10), inset -1px 0 0 rgba(255,255,255,0.10), 0 1px 3px rgba(0,0,0,0.7), 0 50px 80px -30px rgba(0,0,0,0.9)' }}>
                {/* width kleiner als der 380px breite Rahmen — soll auch beim Handy nie breiter
                    als das Gerät selbst wirken. */}
                {onNavigate && <DomainBarMount domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} width={330} collapsed={domainBarCollapsed} onToggleCollapse={onToggleDomainBar} />}
                <div style={{ position: 'absolute', left: '-3px', top: '120px', width: '3px', height: '34px', borderRadius: '2px 0 0 2px', background: 'linear-gradient(90deg,#0e0f11,#4c5157)' }} />
                <div style={{ position: 'absolute', left: '-3px', top: '170px', width: '3px', height: '58px', borderRadius: '2px 0 0 2px', background: 'linear-gradient(90deg,#0e0f11,#4c5157)' }} />
                <div style={{ position: 'absolute', left: '-3px', top: '244px', width: '3px', height: '58px', borderRadius: '2px 0 0 2px', background: 'linear-gradient(90deg,#0e0f11,#4c5157)' }} />
                <div style={{ position: 'absolute', right: '-3px', top: '160px', width: '3px', height: '80px', borderRadius: '0 2px 2px 0', background: 'linear-gradient(270deg,#0e0f11,#4c5157)' }} />

                <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '42px', overflow: 'hidden', background: '#000', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.06)' }}>
                    <iframe ref={iframeRef} key={reloadKey} src={fullUrl} onLoad={onIframeLoad} title="Preview" style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#111' }} />
                    <div style={{ position: 'absolute', left: '50%', top: '14px', transform: 'translateX(-50%)', width: '96px', height: '28px', borderRadius: '16px', background: '#000', boxShadow: '0 0 0 1px rgba(255,255,255,0.05)', zIndex: 2 }} />
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: 'inset 0 0 60px rgba(0,0,0,0.35), inset 0 0 16px rgba(0,0,0,0.3)' }} />
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', mixBlendMode: 'screen', opacity: 0.45, background: 'linear-gradient(106deg, rgba(214,226,244,0.16) 0%, rgba(190,205,228,0.05) 11%, rgba(255,255,255,0) 27%, rgba(255,255,255,0) 68%, rgba(200,214,236,0.04) 88%, rgba(214,226,244,0.10) 100%)' }} />
                    <div style={{ position: 'absolute', left: '-10%', top: '-20%', width: '55%', height: '90%', pointerEvents: 'none', mixBlendMode: 'screen', opacity: 0.35, transform: 'rotate(-14deg)', background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.03) 40%, rgba(255,255,255,0) 70%)', filter: 'blur(22px)' }} />
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(255,255,255,0.04)' }} />
                    <BootOverlay ready={ready} label={title} fontSize={11} />
                </div>

                {showReloadPanel && <MiniReloadPanel onClick={onReloadClick} size={46} gap={22} />}
            </div>

            <div style={{ width: '280px', height: '30px', marginTop: '-14px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 72%)', filter: 'blur(11px)' }} />
        </div>
    );
}

// Passendes "hübscheres" Handy-Mockup im selben Look (Alu-Kante, Glas-Glare, Dynamic
// Island, Seitentasten, weicher Kontaktschatten) — keine Design-Vorlage dafür vorhanden,
// selbst im Stil der Monitor-Vorlage gebaut. Eigenständiger Handy-Modus.
function RealisticPhone({ fullUrl, onIframeLoad, title, domainSlug, subPath, onNavigate, onPin, onBack, canGoBack, subpages, domainBarCollapsed, onToggleDomainBar }) {
    const containerRef = useRef(null);
    // +90px Referenzhöhe für die Adressleiste über dem Rahmen, gleicher Grund wie bei
    // RealisticMonitor.
    const scale = useScaleToFit(containerRef, 620, 990);
    const [reloadKey, setReloadKey] = useState(0);
    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', left: '50%', top: '55%', transform: `translate(-50%,-50%) scale(${scale})`, transformOrigin: 'center center' }}>
                <PhoneDevice fullUrl={fullUrl} onIframeLoad={onIframeLoad} title={title} reloadKey={reloadKey} onReloadClick={() => setReloadKey(k => k + 1)} domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={onToggleDomainBar} />
            </div>
        </div>
    );
}

// "Komplett"-Modus: Monitor und Handy nebeneinander, in einem gemeinsamen
// Skalierungs-Container (dadurch immer im selben Verhältnis zueinander, egal wie
// groß das Vorschau-Fenster ist) und mit EINEM gemeinsamen Reload-Zähler — ein Klick
// auf das eine sichtbare Mini-Panel lädt beide Geräte synchron neu.
// alignItems:'flex-start' statt 'center': beide Bildschirme starten direkt im Rahmen
// (nur 5px/12px Padding Unterschied), zentriert nach der GESAMTHÖHE (inkl. Monitor-
// Standfuß) würde der Handy-Screen sonst deutlich tiefer als der Monitor-Screen sitzen.
// Scroll-Sync: beim Scrollen in einem Gerät wird die relative Scroll-Position (nicht
// die absolute Pixelzahl — die Seiten sind bei Desktop/Handy unterschiedlich lang)
// aufs andere Gerät übertragen. syncingRef verhindert eine Rückkopplungsschleife
// zwischen den beiden scroll-Listenern.
function CombinedDevices({ fullUrl, onIframeLoad, title, domainSlug, subPath, onNavigate, onPin, onBack, canGoBack, subpages, zoomMode = false, domainBarCollapsed, onToggleDomainBar }) {
    const containerRef = useRef(null);
    // +100px Referenzhöhe, gleicher Grund wie in RealisticMonitor: Platz für die
    // über den Monitor-Rahmen hinausragende Adressleiste einplanen. Im Zoom-Vollbild
    // fällt der Monitor-Standfuß weg (hideStand) — kleinere Referenzhöhe genügt dann.
    const scale = useScaleToFit(containerRef, 2300, zoomMode ? 1050 : 1460);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = () => setReloadKey(k => k + 1);

    const monitorIframeRef = useRef(null);
    const phoneIframeRef = useRef(null);
    const syncingRef = useRef(false);

    const mirrorScroll = (sourceEl, targetEl) => {
        try {
            const sWin = sourceEl.contentWindow, tWin = targetEl.contentWindow;
            const sDoc = sWin.document.documentElement, tDoc = tWin.document.documentElement;
            const maxS = sDoc.scrollHeight - sWin.innerHeight;
            const maxT = tDoc.scrollHeight - tWin.innerHeight;
            if (maxS <= 0 || maxT <= 0) return;
            const ratio = sWin.scrollY / maxS;
            syncingRef.current = true;
            // behavior explizit 'instant' setzen: viele Kundenseiten haben eigenes CSS
            // scroll-behavior:smooth — das gilt laut Spezifikation für JEDEN scrollTo-Aufruf
            // (auch die alte scrollTo(x,y)-Form), nicht nur für Nutzer-Scrolling. Ohne die
            // explizite Übersteuerung würde unser Sync-Scroll animiert statt sofort
            // ausgeführt, was mit den laufenden scroll-Events zur Rückkopplung führt.
            tWin.scrollTo({ left: 0, top: Math.round(ratio * maxT), behavior: 'instant' });
            requestAnimationFrame(() => { syncingRef.current = false; });
        } catch { /* Sollte bei gleicher Origin nicht passieren, sicherheitshalber abgefangen */ }
    };

    // scroll-Events feuern beim Wischen/Scrollen sehr häufig (viele Male pro Sekunde) —
    // ohne Drosselung würde bei jedem einzelnen Event synchron eine Layout-Messung
    // (scrollHeight) UND ein Scroll im jeweils anderen iframe ausgelöst, das ruckelt
    // spürbar. Per requestAnimationFrame gebündelt läuft das höchstens einmal pro Frame.
    const attachScrollMirror = (sourceRef, targetRef) => {
        const el = sourceRef.current;
        if (!el) return;
        try {
            let ticking = false;
            el.contentWindow.addEventListener('scroll', () => {
                if (syncingRef.current || ticking) return;
                ticking = true;
                requestAnimationFrame(() => {
                    ticking = false;
                    if (targetRef.current) mirrorScroll(el, targetRef.current);
                });
            }, { passive: true });
        } catch { /* ... */ }
    };

    const handleMonitorLoad = (e) => { onIframeLoad(e); attachScrollMirror(monitorIframeRef, phoneIframeRef); };
    const handlePhoneLoad = (e) => { onIframeLoad(e); attachScrollMirror(phoneIframeRef, monitorIframeRef); };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            {/* top:62% statt 50% -> Block sitzt bewusst etwas unterhalb der Mitte, sonst
                wirkt es (v.a. mit alignItems:flex-start) oben zu knapp/gequetscht. */}
            <div style={{ position: 'absolute', left: '50%', top: '62%', transform: `translate(-50%,-50%) scale(${scale})`, transformOrigin: 'center center', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '90px' }}>
                <MonitorDevice fullUrl={fullUrl} onIframeLoad={handleMonitorLoad} title={title} reloadKey={reloadKey} onReloadClick={reload} showReloadPanel={false} iframeRef={monitorIframeRef} domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} hideStand={zoomMode} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={onToggleDomainBar} />
                <PhoneDevice fullUrl={fullUrl} onIframeLoad={handlePhoneLoad} title={title} reloadKey={reloadKey} onReloadClick={reload} showReloadPanel iframeRef={phoneIframeRef} domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={onToggleDomainBar} />
            </div>
        </div>
    );
}

// Echte Browser-Adressleiste (helles Glas, wie eine reale URL-Zeile) statt eines
// generischen dunklen Eingabefelds — sitzt direkt am Monitorrahmen (siehe MonitorDevice),
// skaliert dadurch mit dem Mockup mit. "www.<slug>.de" wird als fixer, nicht editierbarer
// Domain-Teil angezeigt (rein optisch — an unserer Vorschau-Route ändert das nichts),
// nur der Pfad danach ist editierbar und steuert tatsächlich, welche Unterseite lädt.
// Eigener lokaler Eingabewert statt direkt den committed subPath zu tippen — sonst würde
// jeder Tastenanschlag sofort das iframe neu laden.
// Kleiner runder Chrome-Button in derselben Alu-Palette wie die Adressleiste — für
// Zurück- und Ein-/Ausblenden-Button, die beide direkt an der Leiste hängen.
function BarChromeButton({ onClick, disabled, title, translucent, children }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title}
            style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                width: '34px', height: '34px', borderRadius: '50%', border: '1px solid rgba(0,0,0,0.4)',
                cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
                background: translucent
                    ? 'linear-gradient(180deg,rgba(154,160,167,0.55) 0%,rgba(107,113,120,0.55) 22%,rgba(69,74,79,0.55) 65%,rgba(43,46,50,0.55) 100%)'
                    : 'linear-gradient(180deg,#9aa0a7 0%,#6b7178 22%,#454a4f 65%,#2b2e32 100%)',
                backdropFilter: translucent ? 'blur(14px)' : 'none',
                WebkitBackdropFilter: translucent ? 'blur(14px)' : 'none',
                color: '#f5f5f5', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 2px 5px rgba(0,0,0,0.4), 0 14px 26px -10px rgba(0,0,0,0.6)',
            }}>
            {children}
        </button>
    );
}

function DomainBar({ domainSlug, subPath, onNavigate, onPin, onBack, canGoBack = false, subpages = [], width = 430, translucent = false, collapsed = false, onToggleCollapse }) {
    const [draft, setDraft] = useState(subPath);
    const [menuOpen, setMenuOpen] = useState(false);
    const wrapRef = useRef(null);
    useEffect(() => { setDraft(subPath); }, [subPath]);

    // Klick außerhalb schließt das Dropdown.
    useEffect(() => {
        if (!menuOpen) return;
        const onClickOutside = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [menuOpen]);

    const submit = (e) => { e.preventDefault(); onNavigate(draft); setMenuOpen(false); };
    const pick = (path) => { onNavigate(path); setMenuOpen(false); };
    // Getippter Pfad, bereinigt für den Vergleich mit der (nur vom Crawler befüllten)
    // subpages-Liste — z.B. "/admin" ist absichtlich nirgendwo verlinkt (Admin-Login der
    // Kundenseite) und wird deshalb NIE vom Crawler gefunden. Statt das automatisch beim
    // Navigieren zu merken (das führte vorher zu Karteileichen durch Tippfehler), gibt es
    // jetzt eine explizite "merken"-Aktion, die der Nutzer bewusst anklicken muss.
    const cleanDraft = (draft || '').trim().replace(/^\/+/, '');
    const canPin = onPin && cleanDraft && !subpages.includes(cleanDraft);
    const pinAndGo = () => { onNavigate(cleanDraft); onPin(cleanDraft); setMenuOpen(false); };

    // width darf entweder eine Zahl (Pixel, z.B. am Monitor/Handy-Rahmen montiert) oder
    // ein fertiger CSS-Wert sein (z.B. "min(340px, calc(100vw - 110px))" im Vollbild —
    // dort schrumpft die Leiste auf schmalen Handy-Bildschirmen, statt das Chat-Icon zu
    // überlappen, das rechts oben in derselben Höhe schwebt).
    const cssWidth = typeof width === 'number' ? `${width}px` : width;

    // Kollabiert: nur noch ein kleiner Kreis-Button an genau derselben Stelle (am Gerät
    // montiert ODER frei schwebend im Vollbild) statt der vollen Leiste — zum Wiedereinblenden.
    // Bewusst NICHT als lose auf der Seite schwebender Button umgesetzt (das wirkte beim
    // Zurück-Button schon unklar/verrutscht, wenn die eigentliche Leiste ganz woanders sitzt).
    if (collapsed) {
        return (
            <BarChromeButton onClick={onToggleCollapse} title="Adressleiste einblenden" translucent={translucent}>
                <Eye size={15} />
            </BarChromeButton>
        );
    }

    // Der Zurück- und der Ausblenden-Button hängen direkt links/rechts an der Adressleiste
    // — wie bei einem echten Browser — statt irgendwo lose auf der Seite zu schweben.
    // Dadurch sitzen sie immer da, wo gerade die Adressleiste ist (am Gerät montiert ODER
    // frei schwebend im Vollbild).
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {onBack && (
                <BarChromeButton onClick={onBack} disabled={!canGoBack} title="Zurück" translucent={translucent}>
                    <ArrowLeft size={15} />
                </BarChromeButton>
            )}
            <div ref={wrapRef} style={{ position: 'relative', width: cssWidth, flexShrink: 0 }}>
            <form onSubmit={submit} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                // Selbe Alu-Metall-Palette wie der Monitorrahmen (statt eines hellen
                // "Browser-Chrome"-Looks) — wirkt dadurch wie ein Teil desselben Bauteils.
                // translucent (Vollbild, frei schwebend statt am Rahmen montiert): dieselbe
                // Palette, aber durchscheinend mit Blur statt deckend.
                background: translucent
                    ? 'linear-gradient(180deg,rgba(154,160,167,0.55) 0%,rgba(107,113,120,0.55) 22%,rgba(69,74,79,0.55) 65%,rgba(43,46,50,0.55) 100%)'
                    : 'linear-gradient(180deg,#9aa0a7 0%,#6b7178 22%,#454a4f 65%,#2b2e32 100%)',
                backdropFilter: translucent ? 'blur(14px)' : 'none',
                WebkitBackdropFilter: translucent ? 'blur(14px)' : 'none',
                border: '1px solid rgba(0,0,0,0.4)', borderRadius: '999px',
                padding: '9px 9px 9px 16px', width: '100%', boxSizing: 'border-box',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), inset 1px 0 0 rgba(255,255,255,0.08), inset -1px 0 0 rgba(255,255,255,0.08), 0 2px 5px rgba(0,0,0,0.4), 0 14px 26px -10px rgba(0,0,0,0.6)',
            }}>
                <Globe size={14} color="#cdd1d5" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', fontFamily: "'Segoe UI', Arial, sans-serif", fontSize: '13.5px' }}>
                    <span style={{ color: '#c2c6ca', flexShrink: 0, whiteSpace: 'nowrap' }}>www.{domainSlug}.de/</span>
                    <input
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onFocus={() => setMenuOpen(true)}
                        placeholder="unterseite"
                        // name+autoComplete: verhindert, dass der Browser hier Zahlungsmethoden/
                        // Adressen vorschlägt (Chrome/Brave ignorieren autocomplete="off" allein
                        // bei manchen Heuristiken, ein unverfänglicher name hilft zusätzlich).
                        name="preview-subpage-path"
                        autoComplete="off"
                        style={{
                            flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none',
                            color: '#f5f5f5', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, marginLeft: '1px',
                        }}
                    />
                </div>
                <button type="button" onClick={() => setMenuOpen(o => !o)} title="Verfügbare Unterseiten" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    width: '26px', height: '26px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.14)', color: '#e5e5e5',
                }}>
                    <ChevronDown size={13} style={{ transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </button>
                <button type="submit" title="Unterseite ansteuern" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    width: '28px', height: '28px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: '#fb923c', color: '#000', boxShadow: '0 1px 3px rgba(251,146,60,0.5)',
                }}>
                    <ArrowRight size={13} />
                </button>
            </form>

            {menuOpen && (
                // Deutlich transparenter (0.45 statt 0.82) + stärkerer Blur — 0.82 wirkte auf
                // dunklem Untergrund praktisch deckend, kaum als Glas erkennbar.
                <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 5,
                    background: 'rgba(58,61,66,0.45)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.18)', borderRadius: '14px',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 12px 30px rgba(0,0,0,0.5)', padding: '6px', maxHeight: '220px', overflowY: 'auto',
                }}>
                    {subpages.length === 0 && !canPin && (
                        <div style={{ padding: '10px 12px', fontSize: '12.5px', color: '#a5a9ad', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
                            Noch keine Unterseiten erkannt
                        </div>
                    )}
                    {subpages.map(p => (
                        <button key={p} type="button" onClick={() => pick(p)}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                            style={{
                                display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
                                cursor: 'pointer', padding: '9px 12px', borderRadius: '9px', boxSizing: 'border-box',
                                color: p === subPath ? '#fb923c' : '#e5e5e5', fontSize: '13px',
                                fontFamily: "'Segoe UI', Arial, sans-serif", fontWeight: p === subPath ? 700 : 400,
                            }}>
                            {p === '' ? '/ (Startseite)' : `/${p}`}
                        </button>
                    ))}
                    {canPin && (
                        // Für Seiten, die absichtlich nirgendwo verlinkt sind (z.B. ein Admin-
                        // Login) — der Crawler kann die nie finden, deshalb hier eine bewusste,
                        // manuelle Aktion statt automatischem (fehleranfälligem) Merken.
                        <button type="button" onClick={pinAndGo}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,146,60,0.14)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left',
                                background: 'none', border: 'none', borderTop: subpages.length > 0 ? '1px solid rgba(255,255,255,0.12)' : 'none',
                                marginTop: subpages.length > 0 ? '4px' : 0, paddingTop: subpages.length > 0 ? '8px' : '9px',
                                cursor: 'pointer', padding: '9px 12px', borderRadius: '9px', boxSizing: 'border-box',
                                color: '#fb923c', fontSize: '13px', fontFamily: "'Segoe UI', Arial, sans-serif", fontWeight: 600,
                            }}>
                            <Plus size={13} /> "/{cleanDraft}" merken
                        </button>
                    )}
                </div>
            )}
            </div>
            {onToggleCollapse && (
                <BarChromeButton onClick={onToggleCollapse} title="Adressleiste ausblenden" translucent={translucent}>
                    <EyeOff size={15} />
                </BarChromeButton>
            )}
        </div>
    );
}

// Zentriert über einem Geräte-Rahmen (Monitor oder Handy) "montiert" — kein Abstand
// (die Andockspitze schiebt sich 2px in den Rahmen hinein), Verbindungsspitze in
// derselben Alu-Metall-Palette wie der Rahmen. Wird von MonitorDevice UND PhoneDevice
// verwendet, damit beide Geräte dieselbe Adressleiste bekommen können.
function DomainBarMount({ domainSlug, subPath, onNavigate, onPin, onBack, canGoBack, subpages, width, collapsed, onToggleCollapse }) {
    return (
        <div style={{ position: 'absolute', left: '50%', bottom: '100%', transform: 'translateX(-50%)', marginBottom: '-2px', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <DomainBar domainSlug={domainSlug} subPath={subPath} onNavigate={onNavigate} onPin={onPin} onBack={onBack} canGoBack={canGoBack} subpages={subpages} width={width} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
            {!collapsed && <div style={{ width: '20px', height: '11px', marginTop: '-1px', background: 'linear-gradient(160deg,#8b9198 0%,#454a4f 100%)', clipPath: 'polygon(50% 100%, 8% 0, 92% 0)', boxShadow: '0 4px 6px rgba(0,0,0,0.4)' }} />}
        </div>
    );
}

export default function PreviewPage() {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
    });
    // 'desktop' | 'mobile' – zwingt das iframe auf eine Handy-Breite, damit die
    // eingebettete Seite ihre eigenen @media-Regeln wie auf einem echten Handy anwendet.
    const [device, setDevice] = useState('desktop');
    // Vollbildmodus: echtes Browser-Fullscreen (Fullscreen API) auf dem Wurzel-Element.
    // Zwei Varianten teilen sich dasselbe Browser-Fullscreen (isFullscreen):
    // - "Vollbild": zeigt nur noch die Kundenseite roh (ohne Monitor-/Handy-Rahmen).
    // - "Zoom" (zoomMockup): behält den Monitor-/Handy-Rahmen, zoomt ihn aber bis an den
    //   Bildschirmrand (useScaleToFit skaliert automatisch auf die jetzt volle Bildschirmgröße,
    //   Seitenverhältnis bleibt dabei erhalten).
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoomMockup, setZoomMockup] = useState(false);
    const rootRef = useRef(null);
    // Unterseite, die die Adressleiste zusätzlich zum Vorschau-Pfad ansteuert (z.B.
    // "/impressum") — wird beim Gerätewechsel (device) bewusst NICHT zurückgesetzt,
    // damit man z.B. auf einer Unterseite bleibt, wenn man zwischen PC/Handy wechselt.
    const [subPath, setSubPath] = useState('');
    // Automatisch aus den <a href>-Links der geladenen Kundenseite erkannte (bzw. manuell
    // besuchte) Unterseiten fürs Dropdown in der Adressleiste — kein manuelles Pflegen pro
    // Projekt nötig. Initial aus sessionStorage geladen, damit sie einen Reload überleben.
    const [subpages, setSubpages] = useState(() => {
        try {
            if (!result?.code) return DEFAULT_SUBPAGES;
            const stored = JSON.parse(sessionStorage.getItem(subpagesStorageKey(result.code)) || '[]');
            return Array.from(new Set([...DEFAULT_SUBPAGES, ...stored])).sort();
        } catch { return DEFAULT_SUBPAGES; }
    });
    useEffect(() => {
        if (!result?.code) return;
        try { sessionStorage.setItem(subpagesStorageKey(result.code), JSON.stringify(subpages)); } catch {}
    }, [subpages, result]);

    const inputRef = useRef(null);
    useEffect(() => { if (!result) inputRef.current?.focus(); }, [result]);

    useEffect(() => {
        const onChange = () => {
            const active = !!document.fullscreenElement;
            setIsFullscreen(active);
            if (!active) setZoomMockup(false);
        };
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const enterFullscreen = (zoom = false) => {
        setZoomMockup(zoom);
        rootRef.current?.requestFullscreen?.();
    };

    // Scrollbalken der eingebetteten Kundenseite ausblenden — Scrollen per Mausrad
    // bleibt möglich, nur die sichtbare Leiste stört die Monitor-Optik nicht mehr.
    // Läuft bei jedem (Neu-)Laden des iframes, auch bei internen Links der Kundenseite.
    const hideIframeScrollbar = (e) => {
        try {
            const doc = e.target.contentDocument;
            if (!doc || doc.getElementById('__hide_scrollbar__')) return;
            const style = doc.createElement('style');
            style.id = '__hide_scrollbar__';
            style.textContent = `
                * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                *::-webkit-scrollbar { width: 0 !important; height: 0 !important; background: transparent !important; }
            `;
            doc.head?.appendChild(style);
        } catch { /* Datei nicht gleiche Origin oder noch nicht bereit — ignorieren */ }
    };

    // Sammelt interne Unterseiten aus den <a href>-Links der geladenen Kundenseite, damit
    // das Adressleisten-Dropdown ohne manuelle Pflege pro Projekt befüllt werden kann.
    // Nur Links, die (nach Auflösung durch den Browser) innerhalb unseres Vorschau-Präfixes
    // liegen, zählen als "intern" — externe Links, mailto:, tel: etc. werden ignoriert.
    const collectSubpages = (e) => {
        try {
            const doc = e.target.contentDocument;
            if (!doc || !result) return;
            const base = `${window.location.origin}${result.path}`;
            const found = new Set();
            doc.querySelectorAll('a[href]').forEach((a) => {
                if (!a.href || !a.href.startsWith(base)) return;
                const rest = a.href.slice(base.length).split(/[?#]/)[0].replace(/^\/+/, '').replace(/\/+$/, '');
                if (ASSET_EXT_RE.test(rest)) return;
                found.add(rest);
            });
            if (found.size === 0) return;
            setSubpages((prev) => Array.from(new Set([...prev, ...found])).sort());
        } catch { /* Datei nicht gleiche Origin oder noch nicht bereit — ignorieren */ }
    };

    const onDeviceIframeLoad = (e) => {
        hideIframeScrollbar(e);
        collectSubpages(e);
    };

    // Verlauf der besuchten Unterseiten (wie der Zurück-Button eines echten Browsers) —
    // unabhängig vom Vollbildmodus, damit der Zurück-Button in beiden Ansichten gleich
    // funktioniert.
    const [subPathHistory, setSubPathHistory] = useState([]);
    // Adressleiste ausblendbar (z.B. im Zoom-Vollbild, um mehr vom eigentlichen Bildschirm
    // zu sehen) — Toggle bleibt beim Moduswechsel erhalten, wird nicht automatisch zurückgesetzt.
    // Statt die Leiste komplett zu entfernen, kollabiert sie zu einem kleinen Button an
    // GENAU derselben Stelle (siehe DomainBar collapsed-Modus) — sonst hätte man keine
    // konsistente Stelle mehr, um sie wieder einzublenden.
    const [domainBarCollapsed, setDomainBarCollapsed] = useState(false);
    const toggleDomainBar = () => setDomainBarCollapsed(v => !v);

    // Navigiert nur die Adressleiste. Merkt sich den Pfad bewusst NICHT automatisch fürs
    // Dropdown — sonst landet jeder Tippfehler oder Testpfad dauerhaft in der Liste,
    // obwohl es die Seite gar nicht gibt. Die Liste wird primär vom Crawler/Link-Scan
    // befüllt (nur real existierende, verlinkte Unterseiten); für absichtlich nirgendwo
    // verlinkte Seiten (z.B. ein Admin-Login) gibt es die explizite pinSubpage()-Aktion.
    const navigateTo = (path) => {
        const clean = (path || '').trim().replace(/^\/+/, '');
        setSubPath((current) => {
            if (clean === current) return current;
            setSubPathHistory((h) => [...h, current]);
            return clean;
        });
    };

    // Ein Schritt zurück im Unterseiten-Verlauf, ohne dabei selbst wieder einen neuen
    // Verlaufseintrag zu erzeugen (sonst könnte man nie wirklich "zu Ende" zurückgehen).
    const goBack = () => {
        setSubPathHistory((h) => {
            if (h.length === 0) return h;
            const next = h.slice(0, -1);
            setSubPath(h[h.length - 1]);
            return next;
        });
    };

    // Bewusstes, manuelles Merken eines Pfads (Klick auf "merken" im Dropdown) — im
    // Unterschied zum alten automatischen Merken (das bei jeder Navigation lief und so
    // auch Tippfehler dauerhaft in der Liste landen ließ) nur auf explizite Nutzeraktion.
    const pinSubpage = (path) => {
        const clean = (path || '').trim().replace(/^\/+/, '');
        if (!clean) return;
        setSubpages((prev) => (prev.includes(clean) ? prev : [...prev, clean].sort()));
    };

    // Findet automatisch ALLE intern verlinkten Unterseiten, nicht nur die, die man zufällig
    // anklickt — wichtig, weil auch KUNDEN (die die Seite nicht kennen und nichts manuell
    // eintippen) die vollständige Liste sehen sollen. Läuft einmal im Hintergrund pro
    // Vorschau-Code: startet bei der Startseite, folgt gefundenen Links (per fetch()+
    // DOMParser, ohne das sichtbare iframe zu beeinflussen) bis zu einer Obergrenze, damit
    // auch Seiten gefunden werden, die erst von einer Unterseite aus verlinkt sind.
    // Findet NICHTS, was nirgendwo verlinkt ist (z.B. eine absichtlich versteckte
    // Admin-Route) — das ist beabsichtigt, nicht diese Funktion soll das abdecken.
    useEffect(() => {
        if (!result) return;
        let cancelled = false;
        const MAX_PAGES = 25;
        const crawl = async () => {
            const base = `${window.location.origin}${result.path}`;
            const visited = new Set();
            const queue = [''];
            const found = new Set();
            while (queue.length > 0 && visited.size < MAX_PAGES && !cancelled) {
                const rel = queue.shift();
                if (visited.has(rel)) continue;
                visited.add(rel);
                try {
                    const res = await fetch(base + rel);
                    const ct = res.headers.get('content-type') || '';
                    if (!res.ok || !ct.includes('text/html')) continue;
                    const html = await res.text();
                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    doc.querySelectorAll('a[href]').forEach((a) => {
                        const href = a.getAttribute('href');
                        if (!href) return;
                        let resolved;
                        try { resolved = new URL(href, base + rel).href; } catch { return; }
                        if (!resolved.startsWith(base)) return;
                        const restPath = resolved.slice(base.length).split(/[?#]/)[0].replace(/^\/+/, '').replace(/\/+$/, '');
                        if (ASSET_EXT_RE.test(restPath)) return;
                        if (!found.has(restPath)) {
                            found.add(restPath);
                            if (!visited.has(restPath)) queue.push(restPath);
                        }
                    });
                } catch { /* einzelne fehlgeschlagene Seite überspringen, Crawl weiterlaufen lassen */ }
            }
            if (!cancelled && found.size > 0) {
                setSubpages((prev) => Array.from(new Set([...prev, ...found])).sort());
            }
        };
        crawl();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result?.code]);

    // Ein geteilter Link (/preview?code=...) übernimmt den Code beim Laden automatisch,
    // damit der Empfänger ihn nicht abtippen muss — zeigt danach exakt dieselbe Ansicht
    // wie eine manuelle Code-Eingabe, kein separater "abgespeckter" Modus.
    useEffect(() => {
        if (result) return;
        const urlCode = new URLSearchParams(window.location.search).get('code');
        if (!urlCode) return;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API}/api/preview/verify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code: urlCode.trim() }),
                });
                if (res.ok) {
                    const data = await res.json();
                    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                    setResult(data);
                    window.history.replaceState({}, '', '/preview'); // Code nicht dauerhaft in der Adresszeile stehen lassen
                } else {
                    setError('Ungültiger Code.');
                }
            } catch {
                setError('Verbindungsfehler.');
            }
            setLoading(false);
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const inputStyle = {
        background: 'var(--bg-secondary)',
        border: '2px solid var(--border)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        fontFamily: 'monospace',
        fontSize: '20px',
        letterSpacing: '0.2em',
        textAlign: 'center',
        textTransform: 'uppercase',
        padding: '14px 16px',
        width: '100%',
        outline: 'none',
        boxSizing: 'border-box',
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/preview/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code.trim() }),
            });
            if (!res.ok) {
                setError('Ungültiger Code.');
                setLoading(false);
                return;
            }
            const data = await res.json();
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            setResult(data);
        } catch {
            setError('Verbindungsfehler.');
        }
        setLoading(false);
    };

    const reset = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        if (result?.code) sessionStorage.removeItem(subpagesStorageKey(result.code));
        setResult(null);
        setCode('');
        setSubPath('');
        setSubpages(DEFAULT_SUBPAGES);
    };

    // Baut aus Basis-Vorschau-URL + eingegebener Unterseite die tatsächliche iframe-URL.
    // Führende Slashes werden entfernt, damit "/impressum" und "impressum" gleich behandelt
    // werden — die Basis-URL endet immer mit "/" (Vorschau-Proxy-Pfad), doppelte Slashes
    // würden sonst den Proxy-Pfad verwirren.
    const joinPreviewUrl = (base, sub) => {
        const clean = (sub || '').trim().replace(/^\/+/, '');
        if (!clean) return base;
        return base.endsWith('/') ? base + clean : `${base}/${clean}`;
    };

    if (result) {
        const fullUrl = `${API}${result.path}`;
        const iframeUrl = joinPreviewUrl(fullUrl, subPath);
        // Rein optischer Domain-Name für die Adressleiste — erstes Wort des Vorschau-Titels
        // (z.B. "Schulverein V1.0 (keine Vorgaben)" -> "schulverein"), keine echte Domain.
        const domainSlug = (result.title || 'vorschau').split(/[\s(]/)[0].toLowerCase().replace(/[^a-z0-9-]/g, '') || 'vorschau';
        // "Vollbild" zeigt die Kundenseite roh (ohne Rahmen); "Zoom" bleibt im selben Browser-
        // Fullscreen, behält aber den Monitor-/Handy-Rahmen und zoomt ihn nur bis an den Rand.
        const showRawFullscreen = isFullscreen && !zoomMockup;

        const DeviceToggle = ({ style }) => (
            <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px', padding: '4px', flexShrink: 0, ...style }}>
                <button onClick={() => setDevice('desktop')} title="PC-Ansicht"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '7px', border: 'none',
                        cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s',
                        background: device === 'desktop' ? 'var(--accent)' : 'transparent',
                        color: device === 'desktop' ? '#000' : 'var(--text-secondary)',
                    }}>
                    <Monitor size={14} /> PC
                </button>
                <button onClick={() => setDevice('mobile')} title="Handy-Ansicht"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '7px', border: 'none',
                        cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s',
                        background: device === 'mobile' ? 'var(--accent)' : 'transparent',
                        color: device === 'mobile' ? '#000' : 'var(--text-secondary)',
                    }}>
                    <Smartphone size={14} /> Handy
                </button>
                <button onClick={() => setDevice('combined')} title="Komplett-Ansicht (PC + Handy zusammen)"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '7px', border: 'none',
                        cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s',
                        background: device === 'combined' ? 'var(--accent)' : 'transparent',
                        color: device === 'combined' ? '#000' : 'var(--text-secondary)',
                    }}>
                    <MonitorSmartphone size={14} /> Komplett
                </button>
            </div>
        );

        // Hintergrund hinter dem Monitor-Mockup (bewusst fix dunkel, unabhängig vom
        // Light/Dark-Theme der Seite — Mockups wirken auf neutralem Studio-Hintergrund am besten).
        const BACKDROP = '#0a0a0a';

        return (
            // marginTop hebt das globale ".App main { padding-top: 56px }" auf (das eigentlich
            // Platz für den jetzt versteckten Seiten-Header reserviert) — sonst wäre diese Seite
            // 56px zu hoch für 100vh und würde die äußere Seite zum Scrollen zwingen.
            <div ref={rootRef} style={{ height: '100vh', maxHeight: '100vh', marginTop: isFullscreen ? 0 : '-56px', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: BACKDROP }}>
                <ChatWidget code={result.code} />

                {!isFullscreen && (
                    <div className="container" style={{ paddingTop: '20px', paddingBottom: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px' }}>
                            <div>
                                <h1 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 2px', color: '#f5f5f5' }}>
                                    {result.title || 'Vorschau'}
                                </h1>
                                <p style={{ margin: 0, fontSize: '13px', color: '#8a8a8a' }}>Diese Seite befindet sich noch in der Entwicklung.</p>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                                    <button onClick={() => enterFullscreen(false)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#8a8a8a', fontSize: '13px', fontFamily: 'inherit', padding: 0 }}>
                                        <Maximize size={14} /> Vollbild
                                    </button>
                                    <button onClick={() => enterFullscreen(true)} title="Monitor/Handy-Rahmen bis an den Bildschirmrand zoomen (Seitenverhältnis bleibt erhalten)" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#8a8a8a', fontSize: '13px', fontFamily: 'inherit', padding: 0 }}>
                                        <ZoomIn size={14} /> Zoom
                                    </button>
                                    <button onClick={reset} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#8a8a8a', fontSize: '13px', fontFamily: 'inherit', padding: 0 }}>
                                        <RefreshCw size={14} /> Anderen Code
                                    </button>
                                    <button onClick={reset} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8a8a8a', fontSize: '13px', fontFamily: 'inherit', padding: 0 }}>
                                        Abmelden
                                    </button>
                                </div>
                            </div>

                            <DeviceToggle />
                        </div>
                    </div>
                )}

                {/* Beide Bereiche bleiben IMMER gemountet — nur CSS display wechselt. Würde
                    man stattdessen (wie vorher) je nach isFullscreen zwischen zwei
                    verschiedenen JSX-Zweigen hin- und herschalten, müsste React beim
                    Wechsel jedes Mal ein komplett neues iframe-Element anlegen, was die
                    Kundenseite von Null neu lädt. So bleibt der Ladezustand erhalten. */}
                <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, display: showRawFullscreen ? 'flex' : 'none' }}>
                        <iframe src={iframeUrl} title="Preview" onLoad={onDeviceIframeLoad} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
                        {/* Schwebende, transparente Adressleiste — gleiche Höhe wie das Chat-Icon
                            (top:16px) statt am Monitorrahmen montiert, da im Vollbild kein Rahmen sichtbar ist.
                            Breite schrumpft auf schmalen Handy-Bildschirmen (min(...)), sonst überlappt
                            sie dort das Chat-Icon, das rechts oben in derselben Höhe schwebt. */}
                        <div style={{ position: 'fixed', top: '16px', left: '20px', zIndex: 1000 }}>
                            <DomainBar domainSlug={domainSlug} subPath={subPath} onNavigate={navigateTo} onPin={pinSubpage} onBack={goBack} canGoBack={subPathHistory.length > 0} subpages={subpages} width="min(340px, calc(100vw - 110px))" translucent collapsed={domainBarCollapsed} onToggleCollapse={toggleDomainBar} />
                        </div>
                    </div>
                    {/* Zoom-Vollbild (isFullscreen && zoomMockup) nutzt denselben Zweig wie die
                        normale Ansicht — useScaleToFit in RealisticMonitor/-Phone/CombinedDevices
                        skaliert den Rahmen automatisch auf die jetzt volle Bildschirmgröße hoch,
                        Seitenverhältnis bleibt dabei zwangsläufig erhalten. padding:0 im Zoom-
                        Vollbild, damit der Rahmen wirklich bis an den Bildschirmrand reicht. */}
                    <div style={{
                        position: 'absolute', inset: 0, display: showRawFullscreen ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                        padding: (isFullscreen && zoomMockup) ? 0 : (device === 'mobile' ? '16px 20px' : '8px 16px'),
                    }}>
                        {device === 'mobile'
                            ? <RealisticPhone fullUrl={iframeUrl} onIframeLoad={onDeviceIframeLoad} title={result.title} domainSlug={domainSlug} subPath={subPath} onNavigate={navigateTo} onPin={pinSubpage} onBack={goBack} canGoBack={subPathHistory.length > 0} subpages={subpages} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={toggleDomainBar} />
                            : device === 'combined'
                                ? <CombinedDevices fullUrl={iframeUrl} onIframeLoad={onDeviceIframeLoad} title={result.title} domainSlug={domainSlug} subPath={subPath} onNavigate={navigateTo} onPin={pinSubpage} onBack={goBack} canGoBack={subPathHistory.length > 0} subpages={subpages} zoomMode={isFullscreen && zoomMockup} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={toggleDomainBar} />
                                : <RealisticMonitor fullUrl={iframeUrl} onIframeLoad={onDeviceIframeLoad} title={result.title} domainSlug={domainSlug} subPath={subPath} onNavigate={navigateTo} onPin={pinSubpage} onBack={goBack} canGoBack={subPathHistory.length > 0} subpages={subpages} zoomMode={isFullscreen && zoomMockup} domainBarCollapsed={domainBarCollapsed} onToggleDomainBar={toggleDomainBar} />}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="section" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
            <div className="container">
                <div style={{ maxWidth: '380px', margin: '0 auto', textAlign: 'center' }}>
                    <div style={{
                        width: '56px', height: '56px', borderRadius: '50%',
                        background: 'rgba(251,146,60,0.1)', border: '1.5px solid var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px',
                    }}>
                        <Lock size={24} color="var(--accent)" />
                    </div>
                    <h1 className="section-title" style={{ fontSize: '26px', marginBottom: '8px' }}>Vorschau</h1>
                    <p className="section-subtitle" style={{ marginBottom: '32px' }}>
                        Gib den Zugangscode ein, um die Vorschau zu sehen.
                    </p>

                    <AnimatePresence>
                        {error && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                style={{
                                    padding: '12px 16px', marginBottom: '16px', overflow: 'hidden',
                                    background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)',
                                    borderRadius: '8px', color: '#ff5050', fontSize: '14px',
                                }}>{error}</motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={submit}>
                        <input
                            ref={inputRef}
                            style={inputStyle}
                            placeholder="CODE"
                            value={code}
                            onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
                            required
                        />
                        <button type="submit" className="btn-primary" disabled={loading}
                            style={{ justifyContent: 'center', width: '100%', marginTop: '16px' }}>
                            {loading ? 'Prüfe…' : 'Ansehen'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
